import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { FinanceTabs, FinanceHeader, money, STATUS_TONE, isScopedFinanceUser, ScopedLedgerNotice } from "./financeShell";

const PRESETS = [
  ["all", "All"], ["draft", "Draft"], ["issued", "Issued"], ["sent", "Sent"],
  ["partially_paid", "Partially Paid"], ["overdue", "Overdue"], ["paid", "Paid"],
  ["overpayment", "Overpayment"],
];

function GenerateModal({ onClose, onDone }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("estimates");
  const [busy, setBusy] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [picked, setPicked] = useState(null); // selected template -> client picker step
  const [clients, setClients] = useState(null);
  const [clientQ, setClientQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => { api.get("/finance/invoiceable").then((r) => setData(r.data)).catch(() => setData({ estimates: [], engagements: [] })); }, []);
  useEffect(() => { if (tab === "template" && templates === null) api.get("/finance/invoice-templates").then((r) => setTemplates(r.data.items)).catch(() => setTemplates([])); }, [tab, templates]);
  useEffect(() => {
    if (!picked) return;
    const t = setTimeout(() => api.get(`/finance/clients?q=${encodeURIComponent(clientQ)}`).then((r) => setClients(r.data.items)).catch(() => setClients([])), 200);
    return () => clearTimeout(t);
  }, [picked, clientQ]);

  const generate = async (kind, id) => {
    setBusy(id);
    try {
      const url = kind === "estimate" ? `/finance/invoices/from-estimate/${id}`
                : kind === "engagement" ? `/finance/invoices/from-engagement/${id}`
                : `/finance/invoices/from-proposal/${id}`;
      const { data: inv } = await api.post(url);
      toast.success(`Invoice ${inv.invoice_number} created`);
      onDone(); navigate(`/finance/invoices/${inv.id}`);
    } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Could not generate invoice"); }
    finally { setBusy(null); }
  };

  const createFromTemplate = async (client) => {
    setBusy(client.id);
    try {
      const { data: inv } = await api.post("/finance/invoices", {
        client_id: client.id, currency: picked.currency || "USD",
        payment_terms: picked.payment_terms || null,
        line_items: (picked.line_items || []).map((li) => ({ item_type: li.item_type || "service", name: li.name, quantity: li.quantity ?? 1, unit_price: li.unit_price ?? 0 })),
      });
      toast.success(`Invoice ${inv.invoice_number} created from preset`);
      onDone(); navigate(`/finance/invoices/${inv.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create invoice"); }
    finally { setBusy(null); }
  };

  const rows = data ? (tab === "estimates" ? (data.estimates || data.proposals || []) : data.engagements) : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="fin-generate-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div><h3 className="font-semibold text-slate-900">Generate Invoice</h3><p className="text-xs text-slate-500">From an accepted estimate, signed engagement, or a saved preset.</p></div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><Icons.X className="h-5 w-5" /></button>
        </div>
        <div className="flex gap-1 px-5 pt-3">
          {[["estimates", "Accepted Estimates"], ["engagements", "Signed Engagements"], ["template", "From Preset"]].map(([k, l]) => (
            <button key={k} onClick={() => { setTab(k); setPicked(null); }} data-testid={`gen-tab-${k}`}
              className={`text-sm px-3 py-1.5 rounded-lg ${tab === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
          ))}
        </div>
        <div className="p-5 overflow-y-auto">
          {tab === "template" ? (
            picked ? (
              <div data-testid="gen-template-client-step">
                <button onClick={() => { setPicked(null); setClients(null); setClientQ(""); }} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><Icons.ArrowLeft className="h-4 w-4" />Back to presets</button>
                <div className="text-sm text-slate-600 mb-2">Create <span className="font-medium text-slate-800">{picked.name}</span> for which client?</div>
                <div className="relative mb-3">
                  <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input value={clientQ} onChange={(e) => setClientQ(e.target.value)} data-testid="gen-client-search" placeholder="Search client…" className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200" />
                </div>
                {clients === null ? <div className="py-8 text-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-[#F5A623] inline" /></div> :
                clients.length === 0 ? <div className="py-8 text-center text-slate-400 text-sm">No clients found.</div> :
                <div className="space-y-2">
                  {clients.map((c) => (
                    <div key={c.id} className="flex items-center justify-between border border-slate-100 rounded-lg px-3.5 py-2.5" data-testid={`gen-client-${c.id}`}>
                      <div><div className="font-medium text-slate-800 text-sm">{c.full_name}</div><div className="text-xs text-slate-400 font-mono">{c.client_number}</div></div>
                      <button onClick={() => createFromTemplate(c)} disabled={busy === c.id} data-testid={`gen-client-create-${c.id}`} className="text-sm bg-[#F5A623] hover:bg-[#e0951a] text-[#171B2E] font-medium rounded-lg px-3 py-1.5 disabled:opacity-60">{busy === c.id ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</button>
                    </div>
                  ))}
                </div>}
              </div>
            ) : (
              templates === null ? <div className="py-10 text-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-[#F5A623] inline" /></div> :
              templates.length === 0 ? <div className="py-10 text-center text-slate-400 text-sm">No invoice presets yet. Create one in Templates → Invoice Presets.</div> :
              <div className="space-y-2" data-testid="gen-template-list">
                {templates.map((t) => {
                  const sum = (t.line_items || []).reduce((s, li) => s + (li.quantity || 0) * (li.unit_price || 0), 0);
                  return (
                    <div key={t.id} className="flex items-center justify-between border border-slate-100 rounded-lg px-3.5 py-3" data-testid={`gen-template-${t.id}`}>
                      <div><div className="font-medium text-slate-800 text-sm">{t.name}</div><div className="text-xs text-slate-400">{(t.line_items || []).length} item(s) · {t.currency} {sum.toLocaleString()}</div></div>
                      <button onClick={() => { setPicked(t); setClients(null); setClientQ(""); }} data-testid={`gen-template-use-${t.id}`} className="text-sm bg-[#F5A623] hover:bg-[#e0951a] text-[#171B2E] font-medium rounded-lg px-3 py-1.5">Use</button>
                    </div>
                  );
                })}
              </div>
            )
          ) : rows === null ? <div className="py-10 text-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-[#F5A623] inline" /></div> :
          rows.length === 0 ? <div className="py-10 text-center text-slate-400 text-sm">Nothing available to invoice here.</div> :
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.id} className="flex items-center justify-between border border-slate-100 rounded-lg px-3.5 py-3" data-testid={`gen-row-${r.id}`}>
                <div>
                  <div className="font-medium text-slate-800 text-sm">{r.client_name || "—"}</div>
                  <div className="text-xs text-slate-400 font-mono">{r.estimate_number || r.proposal_number || r.engagement_number} · {money(r.total, r.currency)}</div>
                </div>
                {r.invoiced ? <span className="text-xs text-emerald-600 flex items-center gap-1"><Icons.Check className="h-3.5 w-3.5" />Invoiced</span> :
                  <button onClick={() => generate(tab === "estimates" ? "estimate" : "engagement", r.id)} disabled={busy === r.id}
                    data-testid={`gen-btn-${r.id}`} className="text-sm bg-[#F5A623] hover:bg-[#e0951a] text-[#171B2E] font-medium rounded-lg px-3 py-1.5 disabled:opacity-60">
                    {busy === r.id ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : "Generate"}</button>}
              </div>
            ))}
          </div>}
        </div>
      </div>
    </div>
  );
}

export default function Invoices() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [showGen, setShowGen] = useState(false);
  const status = sp.get("status") || "all";
  const scoped = isScopedFinanceUser(user);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (status && status !== "all") params.set("status", status);
    if (q) params.set("q", q);
    api.get(`/finance/invoices?${params}`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [status, q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  return (
    <div className="p-6 max-w-[1500px] mx-auto space-y-5" data-testid="finance-invoices">
      <FinanceHeader title="Invoices" subtitle={scoped ? "Invoices within your authorized engagement scope." : "All invoices across the firm — drafts, issued, paid and overdue."}>
        <button onClick={() => setShowGen(true)} data-testid="fin-generate-invoice-btn"
          className="inline-flex items-center gap-2 bg-[#171B2E] hover:bg-[#222842] text-white text-sm rounded-lg px-4 py-2.5">
          <Icons.Plus className="h-4 w-4 text-[#F5A623]" />Generate Invoice
        </button>
      </FinanceHeader>
      <FinanceTabs user={user} />
      {scoped && <ScopedLedgerNotice teamScope={user?.finance_team_scope} />}

      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map(([val, label]) => (
          <button key={val} onClick={() => setSp(val === "all" ? {} : { status: val })} data-testid={`inv-preset-${val}`}
            className={`text-sm px-3 py-1.5 rounded-lg border ${status === val ? "border-[#F5A623] bg-amber-50 text-amber-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>{label}</button>
        ))}
        <div className="relative flex-1 min-w-[200px]">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="inv-search" placeholder="Search client or invoice #…" className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap" data-testid="inv-table">
          <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
            {["Invoice #", "Client", "Source", "Total", "Paid", "Balance", "Status", "Due", ""].map((h) => <th key={h} className="px-3 py-2.5 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {items === null ? <tr><td colSpan={9} className="px-5 py-12 text-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-[#F5A623] inline" /></td></tr> :
            items.length === 0 ? <tr><td colSpan={9} className="px-5 py-12 text-center text-slate-400">No invoices found.</td></tr> :
            items.map((i) => (
              <tr key={i.id} onClick={() => navigate(`/finance/invoices/${i.id}`)} data-testid={`inv-row-${i.id}`} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer">
                <td className="px-3 py-2.5 font-mono text-xs text-slate-700">{i.invoice_number}</td>
                <td className="px-3 py-2.5"><div className="font-medium text-slate-800">{i.client_name || "—"}</div><div className="text-xs text-slate-400">{i.client_number}</div></td>
                <td className="px-3 py-2.5 capitalize text-slate-500">{i.source}</td>
                <td className="px-3 py-2.5 font-medium text-slate-800">{money(i.total, i.currency)}</td>
                <td className="px-3 py-2.5 text-slate-600">{money(i.amount_paid, i.currency)}</td>
                <td className="px-3 py-2.5 font-medium text-slate-800">{money(i.balance, i.currency)}</td>
                <td className="px-3 py-2.5">
                  <div className="flex flex-col gap-0.5">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize w-fit ${STATUS_TONE[i.status] || "bg-slate-100"}`}
                      data-testid={`inv-status-badge-${i.id}`}>
                      {i.status.replace(/_/g, " ")}
                    </span>
                    {i.status === "overpayment" && (
                      <span className="text-[10px] font-semibold text-orange-600 flex items-center gap-0.5"
                        data-testid={`inv-overpayment-flag-${i.id}`}>
                        <Icons.AlertTriangle className="h-3 w-3 shrink-0" />
                        Overpaid {money(Math.abs(i.balance ?? 0), i.currency)} — Resolution Required
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2.5 text-slate-500">{i.due_date ? <span className={i.overdue ? "text-rose-600 font-semibold" : ""}>{i.due_date.slice(0, 10)}</span> : "—"}</td>
                <td className="px-3 py-2.5 text-right"><Icons.ChevronRight className="h-4 w-4 text-slate-300 inline" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showGen && <GenerateModal onClose={() => setShowGen(false)} onDone={() => { setShowGen(false); load(); }} />}
    </div>
  );
}
